import React, { memo, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

// COMPONENTES DE ARQUITECTURA MODULAR COMUNES
import SortableHeader from '../TablerosGestion/common/SortableHeader';
import PaginatorFooter from '../TablerosGestion/common/PaginatorFooter';
import ColumnSelector from '../TablerosGestion/common/ColumnSelector';
import TableContainer from '../TablerosGestion/common/TableContainer';
import Icons from '../TablerosGestion/common/Icons';

import ModalDetalleProspectoWeb from './ModalDetalleProspectoWeb';
import { useProspectosWebTable, ACTIONS_COLUMN_WIDTH } from './useProspectosWebTable';

const TableRow = memo(({ item, columnVisibility, onView }) => (
    <tr className="group transition-all duration-75 border-b border-slate-100 hover:bg-slate-50">
        {columnVisibility.fechaCreacion && (
            <td className="py-3.5 px-4 border-b border-r border-slate-100 text-center text-xs text-slate-500 tabular-nums">
                {item.fechaCreacion ? item.fechaCreacion.toLocaleDateString('es-AR') : '-'}
            </td>
        )}
        {columnVisibility.displayNombre && (
            <td className="py-3.5 px-4 border-b border-r border-slate-100 text-sm font-bold text-slate-900 truncate uppercase text-left" title={item.displayNombre}>
                {item.displayNombre}
            </td>
        )}
        {columnVisibility.documento && (
            <td className="py-3.5 px-4 border-b border-r border-slate-100 text-center text-xs font-mono text-slate-500">
                {item.documento || '-'}
            </td>
        )}
        {columnVisibility.telefono && (
            <td className="py-3.5 px-4 border-b border-r border-slate-100 text-center text-xs font-mono text-slate-600">
                {item.telefono || '-'}
            </td>
        )}
        {columnVisibility.email && (
            <td className="py-3.5 px-4 border-b border-r border-slate-100 text-left text-xs text-slate-600 truncate" title={item.email}>
                {item.email || '-'}
            </td>
        )}
        {columnVisibility.vehiculo && (
            <td className="py-3.5 px-4 border-b border-r border-slate-100 text-left text-xs font-bold text-slate-700 uppercase truncate" title={item.vehiculo}>
                {item.vehiculo}
            </td>
        )}
        {columnVisibility.localidad && (
            <td className="py-3.5 px-4 border-b border-r border-slate-100 text-left text-[11px] font-bold text-slate-500 uppercase truncate" title={item.localidad}>
                {item.localidad || '-'}
            </td>
        )}
        {columnVisibility.cantidadCotizaciones && (
            <td className="py-3.5 px-4 border-b border-r border-slate-100 text-center">
                <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-black tabular-nums">
                    {item.cantidadCotizaciones}
                </span>
            </td>
        )}
        {columnVisibility.dispositivo && (
            <td className="py-3.5 px-4 border-b border-r border-slate-100 text-center text-[10px] font-bold text-slate-500 uppercase">
                {item.dispositivo || '-'}
            </td>
        )}

        {/* Acciones */}
        <td className="py-3.5 px-4 border-b border-slate-100 text-center sticky right-0 z-20 bg-white group-hover:bg-slate-50 border-l border-slate-100 shadow-[-10px_0_15px_-5px_rgba(0,0,0,0.03)]">
            <div className="flex justify-center items-center gap-1.5">
                <button
                    type="button"
                    onClick={() => onView(item)}
                    className="p-1 text-slate-400 hover:text-blue-600 transition-all active:scale-90 cursor-pointer"
                    title="Ver datos de la cotización"
                >
                    <Icons.Eye />
                </button>
            </div>
        </td>
    </tr>
));
TableRow.displayName = 'TableRow';

const ProspectosAutos = () => {
    const navigate = useNavigate();
    const {
        loading,
        error,
        rows,
        totalRows,
        totalLeads,
        searchTerm,
        handleSearch,
        sortConfig,
        handleSort,
        columnVisibility,
        toggleColumn,
        columnLabels,
        currentPage,
        totalPages,
        setCurrentPage,
        selectedLead,
        setSelectedLead,
        columnWidths,
        startResize
    } = useProspectosWebTable();

    const visibleColumns = useMemo(
        () => Object.keys(columnLabels).filter((key) => columnVisibility[key]),
        [columnLabels, columnVisibility]
    );

    // La tabla mide exactamente lo que suman sus columnas. Si fuera más ancha, el navegador
    // reparte el sobrante y los anchos dejan de coincidir con lo que el usuario arrastró.
    const totalWidth = useMemo(
        () => visibleColumns.reduce((acc, key) => acc + columnWidths[key], 0) + ACTIONS_COLUMN_WIDTH,
        [visibleColumns, columnWidths]
    );

    return (
        <div className="flex h-screen w-full flex-col overflow-hidden bg-white font-['Inter'] antialiased text-left text-slate-900">
            <div className="h-1 w-full bg-[#01032d] shrink-0"></div>

            {/* CABECERA UNIFICADA */}
            <header className="flex h-20 shrink-0 items-center justify-between border-b border-slate-200 px-8 bg-white z-[110]">
                <div className="flex items-center gap-6">
                    <button
                        type="button"
                        onClick={() => navigate('/cpanel')}
                        className="p-2 text-slate-400 hover:text-slate-800 hover:bg-slate-50 rounded-lg transition-all border border-transparent hover:border-slate-200 shadow-sm cursor-pointer"
                    >
                        <Icons.ArrowLeft />
                    </button>
                    <div>
                        <h2 className="text-xl font-bold text-slate-800 uppercase tracking-tight">Prospectos Autos</h2>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                            Leads del cotizador web
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <div className="relative">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                            placeholder="Buscar por nombre, email, vehículo..."
                            className="w-72 pl-10 pr-4 py-2.5 rounded-full border border-slate-200 text-sm outline-none focus:border-sky-400 focus:ring-4 focus:ring-sky-50 transition-all"
                        />
                        <svg className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                    <ColumnSelector
                        columnVisibility={columnVisibility}
                        columnLabels={columnLabels}
                        toggleColumn={toggleColumn}
                    />
                </div>
            </header>

            {/* CUERPO */}
            <main className="flex-grow overflow-hidden flex flex-col px-6 py-4 gap-3">
                {loading ? (
                    <div className="flex-grow flex items-center justify-center">
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 animate-pulse">
                            Cargando prospectos...
                        </span>
                    </div>
                ) : error ? (
                    <div className="flex-grow flex flex-col items-center justify-center gap-2 px-8 text-center">
                        <p className="text-sm font-bold text-red-600">No se pudieron leer los prospectos.</p>
                        <p className="text-xs text-slate-500 max-w-lg">{error}</p>
                    </div>
                ) : totalLeads === 0 ? (
                    <div className="flex-grow flex flex-col items-center justify-center gap-2">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                            Todavía no hay prospectos del cotizador
                        </p>
                        <p className="text-xs text-slate-400">
                            Cada visitante que cotiza un seguro de auto aparece acá.
                        </p>
                    </div>
                ) : (
                    <>
                        <TableContainer minWidth={`${totalWidth}px`}>
                            <colgroup>
                                {visibleColumns.map((key) => (
                                    <col key={key} style={{ width: `${columnWidths[key]}px` }} />
                                ))}
                                <col style={{ width: `${ACTIONS_COLUMN_WIDTH}px` }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    {Object.entries(columnLabels).map(([key, label]) => (
                                        <SortableHeader
                                            key={key}
                                            label={label}
                                            sortKey={key}
                                            sortConfig={sortConfig}
                                            columnVisibility={columnVisibility}
                                            onSort={handleSort}
                                            onResizeStart={(event) => startResize(key, event)}
                                        />
                                    ))}
                                    <th className="p-4 text-center text-[10px] font-semibold uppercase tracking-wider text-white sticky right-0 top-0 z-40 bg-slate-900 border-b border-l border-slate-700">
                                        Acciones
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((item) => (
                                    <TableRow
                                        key={item.id}
                                        item={item}
                                        columnVisibility={columnVisibility}
                                        onView={setSelectedLead}
                                    />
                                ))}
                            </tbody>
                        </TableContainer>

                        <PaginatorFooter
                            currentPage={currentPage}
                            totalPages={totalPages}
                            currentLength={rows.length}
                            totalLength={totalRows}
                            onPageChange={setCurrentPage}
                            label="prospectos"
                            accentClass="text-sky-900 bg-sky-50 border-sky-100"
                        />
                    </>
                )}
            </main>

            {selectedLead && (
                <ModalDetalleProspectoWeb row={selectedLead} onClose={() => setSelectedLead(null)} />
            )}
        </div>
    );
};

export default ProspectosAutos;
